import { useCallback, useMemo } from 'react';

import orderBy from 'lodash/orderBy';
import { useField, useForm } from 'react-final-form';

import { Button, Typography, Stack, tokens } from '@cast/design-system';

import { useActiveClusters } from 'features/organization/_hooks';

import { FormModel } from './types';

import { RffMultiAutocompleteBadgeCount } from '@cast/shared-rff-ui';

type ClusterOption = {
  id: string;
  name: string;
};

export const ClusterSelect = () => {
  const { clusters } = useActiveClusters();
  const { input } = useField<string[]>('clusterIds');
  const form = useForm<FormModel>();

  const handleDeselectAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      form.change('clusterIds', []);
    },
    [form]
  );

  const hasSelectedClusters = (input.value?.length ?? 0) > 0;

  const items: ClusterOption[] = useMemo(() => {
    return orderBy(
      (clusters || []).map((cluster) => ({
        id: cluster.id,
        name: cluster.name,
      })),
      ['name'],
      ['asc']
    );
  }, [clusters]);

  const clusterLabel = (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
    >
      <Typography variant="P12R">Clusters</Typography>
      {hasSelectedClusters && (
        <Button
          variant="text"
          sx={{ minWidth: 'unset', lineHeight: 1 }}
          onClick={handleDeselectAll}
          testId="deselect-all-clusters"
        >
          Deselect all
        </Button>
      )}
    </Stack>
  );

  return (
    <RffMultiAutocompleteBadgeCount<ClusterOption>
      testId="alert-cluster-select"
      name="clusterIds"
      size="medium"
      label={clusterLabel}
      placeholder="All clusters"
      indicatorLabel="Selected"
      displayChips
      chipSx={{
        backgroundColor: tokens.theme.bg.selection.primary,
        color: tokens.theme.fg.selection.onPrimary,
      }}
      items={items}
      getStringValue={(item) => item.id}
      getLabel={(item) => item.name}
      filterItems={({ items, searchInputValue }) => {
        const query = searchInputValue.toLowerCase();
        return items.filter(
          (item) =>
            item.name.toLowerCase().includes(query) ||
            item.id.toLowerCase().includes(query)
        );
      }}
      renderMenuItem={({ item }) => (
        <Stack direction="column">
          <Typography variant="P14R">{item.name}</Typography>
          <Typography variant="P12R" color="textSecondary">
            {item.id}
          </Typography>
        </Stack>
      )}
    />
  );
};
